/* "Our Cochin" — special editorial article linked from the Kochi travel guide. */
export const OUR_COCHIN = {
  slug: "our-cochin",
  title: "Kochi, Cochin, Ernakulam",
  subtitle: "A City of Three Names and a Thousand Stories",
  heading: "Our Cochin: The Kochi and Ernakulam We Remember",
  intro: `Where pepper once sailed to distant empires, fishing nets rise against Arabian Sea sunsets, ferries cross a restless harbour, old streets remember Portuguese and Dutch footsteps, and a modern Metro glides above roads once ruled by bicycles, Ambassadors and Royal Enfield Bullets.

There are cities you visit.
There are cities you live in.
And then there are cities that seem to accumulate inside you — in smells, sounds, food, friendships, monsoon memories and stories that become more entertaining every time they are retold.

Kochi is one of those cities.

Call it Kochi, as it is officially known today. Call it Cochin, the name by which generations of Indians and travelers around the world knew it. Call the mainland commercial heart Ernakulam, as local people still commonly do.

Whatever name you choose, this extraordinary corner of Kerala has spent centuries looking simultaneously toward the sea and toward the future.

For visitors, the names can initially be confusing. Ernakulam is the district as well as the name commonly associated with the mainland urban center, while Kochi is the larger port city. In everyday conversation the names Kochi, Cochin and Ernakulam are often used somewhat interchangeably, even though they do not mean precisely the same thing.

And perhaps that is appropriate.
Kochi has never been just one thing.`,
  sections: [
    {
      h: "The Flood That Helped Create a City",
      t: `The story of Kochi begins, appropriately enough, with water.

For centuries, Muziris, generally associated with the Kodungallur region north of modern Kochi, was one of the great trading gateways of the Malabar Coast. Ships and merchants connected this part of Kerala with distant civilizations, helping make pepper and other spices internationally coveted commodities.

Then came the great transformation traditionally dated to 1341.

A massive flood altered the Periyar river system. Muziris declined as a major port, while conditions around Kochi helped create or enlarge a natural harbour. Traders gradually shifted toward the emerging port.

Kochi's fortunes changed with the water.

By the fifteenth century, Kochi had become an increasingly important trading center, with commercial links extending to Chinese and Arab merchants.

Pepper was not merely something sprinkled over food.
It was international business.
Spices helped connect Kerala to a commercial world stretching far beyond India's shores.

The excellent Muziris Heritage Project today helps visitors explore this much older chapter of Kerala's maritime history.`,
    },
    {
      h: "When the World Came Looking for Kerala",
      t: `If you stand in Fort Kochi today and see tourists wandering through its old streets, it is tempting to think international tourism is a recent phenomenon.

It isn't.

Foreigners have been arriving on this coast for centuries — although earlier visitors were more likely to arrive looking for pepper, trade, political influence or territory than beach resorts and Instagram photographs.

Portuguese influence became firmly established in Kochi in the early sixteenth century. The Dutch captured Cochin from the Portuguese in 1663, and British control followed in 1795.

The result was not the replacement of one culture by another, but layer upon layer of history.

That is still visible around Fort Kochi and Mattancherry.
Churches, synagogues, mosques, temples, colonial buildings, warehouses, old trading houses and traditional communities exist remarkably close to one another.

Kochi did not suddenly become cosmopolitan in the twenty-first century.
It had been practicing for hundreds of years.`,
    },
    {
      h: "Fort Kochi: Where History Refuses to Leave",
      t: `Fort Kochi is best experienced slowly.

Walk rather than rush.
Look upward at old façades. Turn into side streets. Stop at a café. Watch a ferry. Listen to Malayalam, English and half a dozen other languages passing around you.

Along the waterfront stand the famous Chinese fishing nets, perhaps the city's most recognizable visual symbol.
Their great wooden frames rise and descend like enormous mechanical birds.
Whether every popular story about their exact origin can be proved is less important than what they have become: part of Kochi's visual identity.
At sunset, silhouetted against the harbour, they provide one of Kerala's classic scenes.

Nearby streets lead into a landscape containing St. Francis Church, colonial-era architecture and the old quarters connecting Fort Kochi with Mattancherry.

This is not history sealed behind museum glass.
People live inside it.
Children go to school through it.
Scooters race through it.
Laundry dries beside it.
Tourists photograph it.
And vendors continue conducting business beneath buildings that have watched several empires come and go.`,
    },
    {
      h: "Mattancherry: The Smell of History",
      t: `If Fort Kochi is where Kochi displays its history, Mattancherry is where you can sometimes smell it.

Pepper. Cardamom. Cloves. Cinnamon. Ginger. Turmeric.

Warehouses and trading establishments once helped move Kerala's spices from the hinterland toward ships bound for distant markets.

Nearby Jew Town and the historic Jewish heritage of Mattancherry tell another remarkable story of Kochi's cultural diversity.

This relatively small geographical area contains evidence of communities and trading relationships that connected Kerala with the Middle East, Europe and Asia long before globalization became a business-school expression.

The spice trade made Kochi international.
But generations of people made it Kochi.`,
    },
    {
      h: "Ernakulam: The City Across the Water",
      t: `Cross from Fort Kochi toward the mainland and another personality emerges.

Ernakulam became the bustling commercial face of the urban region — offices, colleges, hospitals, shops, cinemas, hotels, restaurants, markets and relentless movement.

If Fort Kochi sometimes invites contemplation, Ernakulam traditionally asks a more practical question:
What are you here to buy?

For generations, Broadway was one of the answers.
The name has always contained a wonderful bit of Kochi humor because parts of Broadway feel anything but broad.
Yet its narrow commercial world seemed capable of selling almost anything.

Textiles. Books. Shoes. Umbrellas. Stationery. Hardware. Spices. Household goods. Perfumes. Imported treasures.
And miscellaneous objects you had absolutely no intention of buying until a shopkeeper convinced you that your future happiness depended upon owning one.

Shopping in old Ernakulam was not merely commerce.
It was conversation.`,
    },
    {
      h: "When M.G. Road Was \u201cSeventy Feet Road\u201d",
      t: `Before today's flyovers, Metro pillars, towers and endless traffic, generations of residents remember M.G. Road by another name:
Seventy Feet Road.

The name itself suggested grandeur.

Here came hotels, restaurants, textile stores, jewellers, banks, cinemas, bakeries and businesses.
M.G. Road became one of the great social stages of Ernakulam.

You did not merely go there to purchase something.
You went there to see and occasionally to be seen.

The city had its famous establishments and its unforgettable characters: men on Royal Enfield Bullets, fashionable youngsters in bell-bottoms, college students, businessmen, sailors, film enthusiasts and people who seemed to know everybody else's business.

Long before Facebook, Kochi had perfected social networking.
It was called the junction.`,
    },
    {
      h: "The Republic of Junctions",
      t: `Older residents rarely needed latitude and longitude.
They had junctions.

Jos Junction. Pallimukku. Ravipuram. Thevara Junction. Padma Junction. Menaka. Convent Junction. South. North. Pullepady. Maharaja's.

Directions could become a uniquely Kerala form of oral literature:
"Go straight until the junction. Turn after the bakery. Not that bakery — the old bakery. Then ask somebody."

And astonishingly, it worked.

Modern Kochi has GPS.
Old Cochin had human beings.`,
    },
    {
      h: "Broadway, Books and a Generation That Read",
      t: `Before information lived inside a phone, discovering something often required effort.

Bookshops mattered. Libraries mattered. Newspapers mattered.

A newly arrived book could travel through a group of friends until its binding had nearly surrendered.

Young readers devoured detective novels, thrillers, philosophy, literature and magazines — occasionally with greater enthusiasm than they devoted to their prescribed textbooks.

Books by authors such as Erle Stanley Gardner, Alistair MacLean, James Hadley Chase and Ayn Rand circulated among young readers hungry for worlds beyond Kerala.

A forbidden novel hidden inside a school textbook was perhaps the twentieth-century equivalent of switching browser tabs when a teacher approached.

The technology changed.
Students did not.`,
    },
    {
      h: "Cochin Went to the Movies",
      t: `Then there was cinema.

Before streaming, watching a major film required commitment.
You had to discover what was showing. Travel to the theatre. Stand in line. Buy a ticket.
And, on unfortunate occasions, watch the ticket window close just before your turn.

The disappointment was character-building.
The excitement was enormous.

Theatres such as Sridhar, Padma and Shenoys became part of Ernakulam's social geography, while cinemas across Fort Kochi and the surrounding region created their own communities of movie lovers.

A major release was not content.
It was an event.

There was no pause button.
Nobody watched half the movie while checking WhatsApp.
The lights went down and, for the next few hours, the outside world disappeared.`,
    },
    {
      h: "Willingdon Island: A Modern Harbour Rises from the Water",
      t: `Few parts of Kochi symbolize human ambition quite like Willingdon Island.

The modern harbour was substantially developed during the early twentieth century under harbour engineer Sir Robert Bristow. Material dredged while creating the shipping channel was used to reclaim land that became Willingdon Island.

The first vessel crossed the newly created channel into the harbour in 1928, and the modern port continued developing through the following years.

The island became associated with port operations, rail and road connections, shipping, hotels and military activity.

For many older Cochin families, however, Willingdon Island meant something more personal.
Evening walks. Harbour breezes. Ships in the distance. Children watching boats.
Families gathering as the fierce afternoon heat surrendered to the Arabian Sea.

There are places that look spectacular in photographs.
And there are places whose beauty depends on having once stood there with someone you loved.
Willingdon Island belongs to both categories.`,
    },
    {
      h: "The Navy and the Sea",
      t: `Kochi's relationship with the sea is commercial, cultural and strategic.

The presence of the Southern Naval Command and INS Venduruthy added another unmistakable dimension to twentieth-century Cochin.
Uniforms, naval establishments, ships and military families became part of the city's social landscape.

For generations of children growing up around the harbour, ships were not exotic objects.
They were neighbors.

The sea was never merely scenery.
It was employment, transportation, trade, defense, food, weather, adventure — and occasionally danger.`,
    },
    {
      h: "A City with a Soundtrack",
      t: `Every generation of Kochi seems to have had its own music.

Carnatic masters filled family homes through vinyl records.
Western popular music arrived through records brought by travelers, sailors, relatives and mysterious informal networks that functioned surprisingly efficiently long before streaming.
Local bands performed at schools, clubs, hotels and social gatherings.

A new record was something to possess, share and discuss.

You could not instantly summon almost every song ever recorded.
You had to find music.
That difficulty gave discovery a certain magic.

A treasured LP might pass through a circle of friends with almost diplomatic protocols governing its return.

Today's listener carries millions of songs in a pocket.
Yesterday's listener might own twenty records and remember exactly where, when and with whom each one was first played.

Which generation was richer?
That question has no easy answer.`,
    },
    {
      h: "Kochi Ate Before Kochi Became a Foodie City",
      t: `Today's Kochi has sophisticated restaurants, international cuisines, boutique cafés and social-media-famous dishes.
But the city has always understood food.

Its kitchens reveal its geography and history.

Fresh fish and seafood. Karimeen. Prawns. Crab. Sardines. Beef preparations. Appam and stew. Puttu and kadala. Porotta. Fish curry. Biriyani. Banana chips. Achappam. Unniyappam. Payasam.
Tea strong enough to revive a defeated cricket team.

Different communities added their own traditions — Syrian Christian, Muslim, Jewish, Konkani, Tamil, Gujarati, Marwari and others.
A port city absorbs flavors just as readily as languages.

And then there were the bakeries.
For generations of children, teenagers and adults, a bakery was capable of defeating every nutritional principle they had been taught at home.
The smell of freshly baked bread, cakes, puffs and pastries could destroy discipline at twenty metres.

Calories were technically present.
But nobody had invented the smartphone app to count them yet, so civilization continued peacefully.`,
    },
    {
      h: "Kochi's Great Talent: Turning Fact into Legend",
      t: `Every old city has official history.
And every interesting city has unofficial history.
Kochi has both in abundance.

Ask several longtime residents about a colorful personality from the 1960s or 1970s and you may receive six versions of the same story.

A student prank becomes an arrest.
An arrest becomes a scandal.
A scooter ride becomes a romance.
A cinema trip becomes a family crisis.
A man who once resembled a film star eventually becomes, in collective memory, the film star's secret competitor.

Did everything happen exactly as remembered?
Almost certainly not.
Does that make the stories worthless?
Quite the opposite.

Urban folklore tells us how a city remembers itself.

But folklore should be labeled as folklore. The outrageous tales involving students, actors, local celebrities and youthful mischief are best enjoyed as part of Kochi's oral storytelling tradition unless supported by reliable evidence.

Never allow a questionable fact to ruin a wonderful Cochin story.
Just don't print it as history.`,
    },
    {
      h: "From Ferries and Ambassadors to Metro Trains",
      t: `Modern Kochi would astonish someone transported directly from the 1960s.

The skyline has changed. Apartment towers have spread. Information technology and service industries have grown. Shopping malls have transformed retail. Traffic has multiplied. The airport connects the region with the world.

And above roads once navigated by bicycles, buses, Fiats, Ambassadors and Bullets runs the Kochi Metro.
The first Aluva–Palarivattom section was inaugurated on June 17, 2017, and opened to passengers on June 19.

The Metro represents the new Kochi: larger, faster and increasingly interconnected.

Yet Kochi's transportation story has also returned to its oldest highway — water — through modern ferry and water-transit development.

Perhaps that is fitting.
The city's future, like its past, keeps coming back to water.`,
    },
    {
      h: "Old Cochin and New Kochi Meet Every Day",
      t: `It would be easy to become sentimental and claim everything was better fifty years ago.
It wasn't.

Modern Kochi offers opportunities, infrastructure, medical care, communications, transportation, education and conveniences earlier generations could scarcely imagine.

But something has undeniably changed.

The older city demanded more physical participation.

To see a friend, you usually went somewhere.
To watch a movie, you queued.
To hear a new record, you found someone who owned it.
To learn something, you searched for a book or asked somebody.
To shop, you walked Broadway.

To know what was happening in the neighborhood, you needed absolutely nothing.
The neighborhood would tell you.
Usually before your parents asked.`,
    },
    {
      h: "The Kochi You Should Experience",
      t: `A visitor should resist treating Kochi as a checklist.

Yes, see Fort Kochi. Watch the Chinese fishing nets. Walk through Mattancherry and Jew Town. Explore the heritage buildings. Ride a ferry. Discover Broadway. Experience M.G. Road. See the harbour. Travel on the Metro. Taste Kerala seafood. Drink tea from a small local shop.
Watch the monsoon if you are fortunate enough to be there when the clouds break.

But leave time for something that does not appear on an itinerary.

Sit somewhere. Watch. Listen.

Kochi reveals itself between attractions.

It is in Malayalam conversations drifting across a tea shop.
The smell of frying shallots and curry leaves.
The horn of a ferry.
Rainwater exploding beneath an autorickshaw tyre.
A fish vendor negotiating at full volume.
Church bells. Temple sounds. The call to prayer.
A ship moving almost silently through the harbour.
A grandmother switching effortlessly between Malayalam and English.
A Gulf-returned family arriving with suitcases.
A student hurrying toward the Metro.
An old man explaining that the building in front of you used to be something entirely different — and then telling you a forty-minute story about it.

Listen to him.
That may be the best tour you take.`,
    },
    {
      h: "Kochi, Cochin or Ernakulam?",
      t: `So which name should a visitor use?
All three have a place.

Kochi is the modern official identity.
Cochin carries centuries of maritime and colonial-era usage and remains alive in the names of important institutions.
Ernakulam identifies the district and is also deeply embedded in everyday references to the mainland commercial city.

The official Ernakulam district administration itself describes the district as combining deep heritage with modern industry and commerce, while noting Kochi's historic identity as the "Queen of the Arabian Sea."

Names change.
Cities do too.
Memory is less cooperative.

Ask someone who grew up here in the 1960s or 1970s about "Cochin," and watch what happens.

The geography disappears.
Memory takes over.

Broadway becomes crowded again.
Seventy Feet Road returns.
The cinemas switch on their lights.
Vinyl begins turning.
A Bullet starts somewhere in the distance.
A ferry crosses the harbour.
Children run along Willingdon Island.
Someone is late getting home.
Someone's mother already knows why.
And the Arabian Sea breeze moves through it all.

That city still exists.
Not completely on a map.
Not completely in history books.

It survives in photographs, old shops, buildings, recipes, songs, family stories and the memories of millions of people who passed through it.

Modern Kochi may have grown upward and outward.
But old Cochin grew inward.
Into the people who lived there.

And perhaps that is why, no matter how dramatically the skyline changes, Cochin never entirely becomes the past.

You can still find it — if you know where to look.`,
    },
  ],
  links: [
    { label: "Kerala Tourism — How Kochi Emerged as an Important Port City", url: "https://www.keralatourism.org/kochi/" },
    { label: "Muziris Heritage Project — Explore the history and heritage of Muziris", url: "https://www.muzirisheritage.org/" },
    { label: "Ernakulam District Administration — Official Ernakulam District Website", url: "https://ernakulam.nic.in/" },
    { label: "Cochin Port Authority", url: "https://cochinport.gov.in/" },
    { label: "Kochi Metro — Kochi Metro Rail Limited", url: "https://kochimetro.org/" },
    { label: "All About Ernakulam (Cochin/Kochi) — Ernakulam.com", url: "https://www.ernakulam.com/" },
    { label: "All About Kerala (Keralam) — Kerala.com", url: "https://www.kerala.com/" },
  ],
};
